import { FormikProps } from 'formik';
import { Card } from '@/components/ui/card';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { OptionTextFields } from './OptionTextFields';
import { OptionImageFields } from './OptionImageFields';
import { OptionThirdImageField } from './OptionThirdImageField';
import { OptionCorrectToggle } from './OptionCorrectToggle';
import { OptionDetailsPanel } from './OptionDetailsPanel';
import type { SelectedFixtureData } from './types';

interface OptionCardProps {
  index: number;
  formik: FormikProps<QuestionFormValues>;
  selectedTeams?: string[];
  selectedFixtureData?: SelectedFixtureData | null;
  translateField: (
    sourceKey: string,
    sourceValue: string,
    targetKey: string,
    setFieldValue: any
  ) => void;
  isFieldTranslating: (key: string) => boolean;
  isEditable: boolean;
  questionValues: QuestionFormValues;
  /** When set, auto-selects this league and disables the league selector in picker modals */
  restrictedLeagueId?: string;
  /** Pickem ID for image upload organization */
  pickemId?: string;
  /** Contest League IP flag — drives licensed vs generic image auto-fill */
  leagueIp?: boolean;
}

export function OptionCard({
  index,
  formik,
  selectedTeams = [],
  selectedFixtureData = null,
  translateField,
  isFieldTranslating,
  isEditable,
  questionValues,
  restrictedLeagueId,
  pickemId,
  leagueIp = false,
}: OptionCardProps) {
  const { errors, touched, values, setFieldValue } = formik;
  const option = values.options[index];

  return (
    <Card className="p-4">
      <div className="grid grid-cols-[75%_25%] gap-4">
        {/* LEFT COLUMN (75%) - Current option content */}
        <div className="space-y-4">
          <OptionTextFields
            index={index}
            optionTextEn={option.option_text_en || ''}
            optionTextFr={option.option_text_fr || ''}
            errors={errors.options?.[index] as any}
            touched={touched.options?.[index]}
            translateField={translateField}
            isFieldTranslating={isFieldTranslating}
            setFieldValue={setFieldValue}
            isEditable={isEditable}
          />

          <OptionImageFields
            index={index}
            optionImageEn={option.option_image_en || ''}
            optionImageFr={option.option_image_fr || ''}
            selectedTeams={selectedTeams}
            selectedFixtureData={selectedFixtureData}
            questionType={questionValues.question_type || ''}
            setFieldValue={setFieldValue}
            isEditable={isEditable}
            restrictedLeagueId={restrictedLeagueId}
            pickemId={pickemId}
            leagueIp={leagueIp}
          />

          <OptionThirdImageField thirdImageEn={option.third_image_en || ''} />

          <OptionCorrectToggle
            index={index}
            isCorrect={option.is_correct}
            setFieldValue={setFieldValue}
            isEditable={isEditable}
          />
        </div>

        {/* RIGHT COLUMN (25%) - New details panel */}
        <OptionDetailsPanel option={option} question={questionValues} />
      </div>
    </Card>
  );
}
